import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QrCode, Landmark, ShieldCheck, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, fmtMoney } from "@/lib/api";

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 text-sm py-1.5"><span className="text-muted-foreground">{k}</span><span className="font-mono text-right break-all">{v || "—"}</span></div>
);

export default function PaymentDialog({ item, kind, open, onOpenChange, onDone }) {
  const [settings, setSettings] = useState(null);
  const [txn, setTxn] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTxn("");
      setNote("");
      api.get("/billing/payment-settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
    }
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/billing/payment-requests", { kind, item_id: item.id, transaction_id: txn, note });
      toast.success("Payment submitted. Pending admin verification.");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!item) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Pay for {item.name}</DialogTitle>
          <DialogDescription>Pay externally via UPI or bank transfer, then submit your transaction ID for verification.</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg bg-primary/5 border border-primary/20 p-4 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{kind === "plan" ? `${item.duration_days} day subscription` : `${item.credits} credits`}</span>
          <span className="font-display text-2xl font-bold" data-testid="payment-amount">{fmtMoney(item.price)}</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-lg border p-4">
            <p className="flex items-center gap-2 font-semibold text-sm mb-3"><QrCode className="h-4 w-4 text-primary" />UPI</p>
            {settings?.qr_image ? (
              <img src={settings.qr_image} alt="UPI QR" className="w-40 h-40 object-contain mx-auto rounded-md border bg-white" data-testid="payment-qr-image" />
            ) : (
              <div className="w-40 h-40 mx-auto rounded-md border border-dashed flex items-center justify-center text-xs text-muted-foreground text-center p-3">QR not configured yet</div>
            )}
            <Row k="UPI ID" v={settings?.upi_id} />
            <Row k="Payee" v={settings?.payee_name} />
          </div>
          <div className="rounded-lg border p-4">
            <p className="flex items-center gap-2 font-semibold text-sm mb-3"><Landmark className="h-4 w-4 text-primary" />Bank transfer</p>
            <Row k="Bank" v={settings?.bank_name} />
            <Row k="Account name" v={settings?.account_name} />
            <Row k="Account no." v={settings?.account_number} />
            <Row k="IFSC" v={settings?.ifsc} />
            {settings?.instructions && <p className="text-xs text-muted-foreground mt-3 leading-relaxed">{settings.instructions}</p>}
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <Label htmlFor="txn">Transaction ID / UTR</Label>
            <Input id="txn" data-testid="payment-transaction-id-input" value={txn} onChange={(e) => setTxn(e.target.value)} placeholder="e.g. 412345678901" className="mt-1.5 font-mono" />
          </div>
          <div>
            <Label htmlFor="pnote">Note (optional)</Label>
            <Textarea id="pnote" data-testid="payment-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1.5" />
          </div>
          <p className="flex items-start gap-2 text-xs text-muted-foreground"><ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0" />Access is granted only after an admin confirms receipt of payment.</p>
          <Button className="w-full" disabled={busy || txn.trim().length < 6} onClick={submit} data-testid="submit-upi-payment-button">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Submit for verification
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
